import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  useCreateReportScheduleMutation,
  useDeleteReportScheduleMutation,
  useGetReportScheduleMetaQuery,
  useGetReportSchedulesQuery,
  useRunReportScheduleNowMutation,
  useUpdateReportScheduleMutation,
} from "../../redux/admin/reportApi";
import { useGetAllLiteDevicesQuery } from "../../redux/admin/adminApi";
import SearchableDeviceSelect from "../../components/common/SearchableDeviceSelect";

const DEFAULT_EMAIL_SLOTS = ["", "", "", "", ""];
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
};

const defaultForm = {
  id: "",
  name: "",
  emails: DEFAULT_EMAIL_SLOTS,
  deviceIds: [],
  reportTypes: [],
  format: "csv",
  frequency: "daily",
  timeOfDay: "08:00",
  dayOfWeek: 1,
  dayOfMonth: 1,
  rangePreset: "yesterday",
  timezone: "Asia/Kolkata",
  isActive: true,
};

const dayOptions = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ReportScheduler = () => {
  const [form, setForm] = useState(defaultForm);
  const [status, setStatus] = useState({ type: "", message: "" });
  const {
    data: meta = {},
    isLoading: metaLoading,
    isFetching: metaFetching,
    refetch: refetchMeta,
  } = useGetReportScheduleMetaQuery();
  const {
    data: schedulesResponse = { schedules: [] },
    isLoading: schedulesLoading,
    isFetching: schedulesFetching,
    refetch: refetchSchedules,
  } = useGetReportSchedulesQuery();
  const [createReportSchedule, { isLoading: creating }] = useCreateReportScheduleMutation();
  const [updateReportSchedule, { isLoading: updating }] = useUpdateReportScheduleMutation();
  const [deleteReportSchedule, { isLoading: deleting }] = useDeleteReportScheduleMutation();
  const [runReportScheduleNow, { isLoading: runningNow }] = useRunReportScheduleNowMutation();

  const schedules = useMemo(
    () => (Array.isArray(schedulesResponse?.schedules) ? schedulesResponse.schedules : []),
    [schedulesResponse]
  );
  // Use the main devices query (same source as dashboard/reports) so the
  // dropdown always shows real devices regardless of what /schedules/meta returns.
  const { data: rawAllDevices = [] } = useGetAllLiteDevicesQuery();
  const devices = useMemo(() => {
    const list = Array.isArray(rawAllDevices) ? rawAllDevices.flat(Infinity) : [];
    // fallback to meta.devices if allDevices is empty (e.g. permissions)
    if (list.length) return list.filter((d) => d && (d._id || d.imei));
    return Array.isArray(meta?.devices) ? meta.devices : [];
  }, [rawAllDevices, meta]);
  const reportTypes = Array.isArray(meta?.reportTypes) ? meta.reportTypes : [];
  const formats = Array.isArray(meta?.formats) && meta.formats.length ? meta.formats : ["csv", "pdf", "xlsx"];
  const frequencies =
    Array.isArray(meta?.frequencies) && meta.frequencies.length ? meta.frequencies : ["daily", "weekly", "monthly"];
  const rangePresets =
    Array.isArray(meta?.rangePresets) && meta.rangePresets.length
      ? meta.rangePresets
      : ["today", "yesterday", "last_7_days", "last_30_days"];

  const resetForm = () => {
    setForm({ ...defaultForm, emails: [...DEFAULT_EMAIL_SLOTS] });
    setStatus({ type: "", message: "" });
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setEmailAt = (index, value) => {
    setForm((prev) => {
      const nextEmails = [...prev.emails];
      nextEmails[index] = value;
      return { ...prev, emails: nextEmails };
    });
  };

  const handleEdit = (schedule) => {
    const nextEmails = [...DEFAULT_EMAIL_SLOTS];
    (schedule.emails || []).slice(0, 5).forEach((email, index) => {
      nextEmails[index] = email;
    });

    setForm({
      id: schedule._id,
      name: schedule.name || "",
      emails: nextEmails,
      deviceIds: (schedule.deviceIds || []).map((entry) => String(entry)),
      reportTypes: schedule.reportTypes || [],
      format: schedule.format || "csv",
      frequency: schedule.frequency || "daily",
      timeOfDay: schedule.timeOfDay || "08:00",
      dayOfWeek: Number(schedule.dayOfWeek ?? 1),
      dayOfMonth: Number(schedule.dayOfMonth ?? 1),
      rangePreset: schedule.rangePreset || "yesterday",
      timezone: schedule.timezone || "Asia/Kolkata",
      isActive: schedule.isActive !== false,
    });
    setStatus({ type: "", message: "" });
  };

  const handleSubmit = async () => {
    try {
      setStatus({ type: "", message: "" });
      const payload = {
        name: form.name,
        emails: toArray(form.emails).filter((entry) => String(entry || "").trim()),
        deviceIds: toArray(form.deviceIds),
        reportTypes: toArray(form.reportTypes),
        format: form.format,
        frequency: form.frequency,
        timeOfDay: form.timeOfDay,
        dayOfWeek: form.frequency === "weekly" ? form.dayOfWeek : null,
        dayOfMonth: form.frequency === "monthly" ? form.dayOfMonth : null,
        rangePreset: form.rangePreset,
        timezone: form.timezone,
        isActive: form.isActive,
      };

      if (form.id) {
        await updateReportSchedule({ id: form.id, ...payload }).unwrap();
        setStatus({ type: "success", message: "Report schedule updated successfully" });
      } else {
        await createReportSchedule(payload).unwrap();
        setStatus({ type: "success", message: "Report schedule created successfully" });
      }

      resetForm();
    } catch (error) {
      setStatus({
        type: "error",
        message: error?.data?.message || error?.message || "Failed to save report schedule",
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      setStatus({ type: "", message: "" });
      await deleteReportSchedule(id).unwrap();
      if (form.id === id) resetForm();
      setStatus({ type: "success", message: "Report schedule deleted" });
    } catch (error) {
      setStatus({
        type: "error",
        message: error?.data?.message || error?.message || "Failed to delete report schedule",
      });
    }
  };

  const handleRunNow = async (id) => {
    try {
      setStatus({ type: "", message: "" });
      await runReportScheduleNow(id).unwrap();
      setStatus({ type: "success", message: "Report schedule executed" });
    } catch (error) {
      setStatus({
        type: "error",
        message: error?.data?.message || error?.message || "Failed to run report schedule",
      });
    }
  };

  const busy = creating || updating || deleting || runningNow;
  const initialPageLoading = metaLoading || schedulesLoading;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      {initialPageLoading ? (
        <Paper
          elevation={0}
          sx={{
            minHeight: 320,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack spacing={1.5} alignItems="center">
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading report scheduler...
            </Typography>
          </Stack>
        </Paper>
      ) : (
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", lg: "flex-start" }}
      >
        <Paper
          elevation={0}
          sx={{
            flex: 1.05,
            p: 2.5,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
            Report Scheduler
          </Typography>

          {status.message ? (
            <Alert severity={status.type || "info"} sx={{ mb: 2 }}>
              {status.message}
            </Alert>
          ) : null}

          <Stack spacing={2}>
              <TextField
                label="Schedule Name"
                size="small"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                fullWidth
              />

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                {form.emails.map((email, index) => (
                  <TextField
                    key={index}
                    label={`Email ${index + 1}`}
                    size="small"
                    value={email}
                    onChange={(e) => setEmailAt(index, e.target.value)}
                    fullWidth
                  />
                ))}
              </Stack>

              <SearchableDeviceSelect
                multiple
                label="Select Device"
                options={devices}
                value={devices.filter((device) =>
                  toArray(form.deviceIds).includes(String(device?._id))
                )}
                onChange={(nextDevices) =>
                  setField(
                    "deviceIds",
                    (Array.isArray(nextDevices) ? nextDevices : []).map((device) => String(device?._id))
                  )
                }
                helperText="Search by vehicle name, IMEI, or plate"
              />

              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, width: 120 }}>Action</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Report Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportTypes.map((report) => {
                      const checked = form.reportTypes.includes(report.value);
                      return (
                        <TableRow key={report.value}>
                          <TableCell>
                            <Checkbox
                              checked={checked}
                              onChange={(e) => {
                                const nextTypes = e.target.checked
                                  ? [...form.reportTypes, report.value]
                                  : form.reportTypes.filter((value) => value !== report.value);
                                setField("reportTypes", nextTypes);
                              }}
                            />
                          </TableCell>
                          <TableCell>{report.label}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Format</InputLabel>
                  <Select
                    label="Format"
                    value={form.format}
                    onChange={(e) => setField("format", e.target.value)}
                  >
                    {formats.map((format) => (
                      <MenuItem key={format} value={format}>
                        {String(format).toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    label="Date Range"
                    value={form.rangePreset}
                    onChange={(e) => setField("rangePreset", e.target.value)}
                  >
                    {rangePresets.map((preset) => (
                      <MenuItem key={preset} value={preset}>
                        {String(preset).replaceAll("_", " ").toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    label="Frequency"
                    value={form.frequency}
                    onChange={(e) => setField("frequency", e.target.value)}
                  >
                    {frequencies.map((frequency) => (
                      <MenuItem key={frequency} value={frequency}>
                        {String(frequency).toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label="Time"
                  type="time"
                  value={form.timeOfDay}
                  onChange={(e) => setField("timeOfDay", e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  size="small"
                  label="Timezone"
                  value={form.timezone}
                  onChange={(e) => setField("timezone", e.target.value)}
                  fullWidth
                />
              </Stack>

              {form.frequency === "weekly" ? (
                <FormControl size="small" fullWidth>
                  <InputLabel>Day of Week</InputLabel>
                  <Select
                    label="Day of Week"
                    value={form.dayOfWeek}
                    onChange={(e) => setField("dayOfWeek", Number(e.target.value))}
                  >
                    {dayOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}

              {form.frequency === "monthly" ? (
                <TextField
                  size="small"
                  type="number"
                  label="Day of Month"
                  value={form.dayOfMonth}
                  onChange={(e) => setField("dayOfMonth", Number(e.target.value))}
                  inputProps={{ min: 1, max: 31 }}
                  fullWidth
                />
              ) : null}

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontWeight: 700 }}>Active Schedule</Typography>
                <Switch
                  checked={Boolean(form.isActive)}
                  onChange={(e) => setField("isActive", e.target.checked)}
                />
              </Stack>

              <Stack direction="row" spacing={1.2}>
                <Button variant="contained" onClick={handleSubmit} disabled={busy}>
                  {busy ? "Saving..." : form.id ? "UPDATE" : "SAVE"}
                </Button>
                <Button variant="outlined" onClick={resetForm}>
                  Reset
                </Button>
                <IconButton
                  onClick={() => {
                    refetchMeta();
                    refetchSchedules();
                  }}
                  disabled={metaFetching || schedulesFetching}
                >
                  <RefreshIcon />
                </IconButton>
              </Stack>
            </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 2.5,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            minHeight: 300,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Saved Schedules
            </Typography>
            <IconButton onClick={refetchSchedules} disabled={schedulesFetching}>
              <RefreshIcon />
            </IconButton>
          </Stack>

          {schedules.length === 0 ? (
            <Alert severity="info">Abhi koi report schedule save nahi hai.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Reports</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Next Run</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Last Run</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedules.map((schedule) => (
                    <TableRow key={schedule._id} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700 }}>{schedule.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(schedule.emails || []).join(", ")}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.5}>
                          {(schedule.reportTypes || []).map((type) => (
                            <Chip key={type} size="small" label={type} />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>{formatDateTime(schedule.nextRunAt)}</TableCell>
                      <TableCell>{formatDateTime(schedule.lastRunAt)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={schedule.lastRunStatus || "never"}
                          color={
                            schedule.lastRunStatus === "success"
                              ? "success"
                              : schedule.lastRunStatus === "partial"
                                ? "warning"
                                : schedule.lastRunStatus === "failed"
                                  ? "error"
                                  : "default"
                          }
                          variant="outlined"
                        />
                        {schedule.lastRunMessage ? (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            {schedule.lastRunMessage}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton color="primary" onClick={() => handleEdit(schedule)} disabled={busy}>
                            <EditIcon />
                          </IconButton>
                          <IconButton color="success" onClick={() => handleRunNow(schedule._id)} disabled={busy}>
                            <PlayArrowIcon />
                          </IconButton>
                          <IconButton color="error" onClick={() => handleDelete(schedule._id)} disabled={busy}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>
      )}
    </Box>
  );
};

export default ReportScheduler;
